import { useLocation, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Home, ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getLocalizedPath, Language } from "@/utils/localizedRoutes";

const translations: Record<Language, {
  title: string;
  subtitle: string;
  description: string;
  homeButton: string;
  backButton: string;
  contactButton: string;
}> = {
  sk: {
    title: "Ups! Stránka sa nenašla",
    subtitle: "Chyba 404",
    description: "Stránka, ktorú hľadáte, neexistuje alebo bola presunutá. Skúste sa vrátiť na domovskú stránku.",
    homeButton: "Späť na úvod",
    backButton: "Späť",
    contactButton: "Kontaktujte nás",
  },
  en: {
    title: "Oops! Page not found",
    subtitle: "Error 404",
    description: "The page you're looking for doesn't exist or has been moved. Try going back to the homepage.",
    homeButton: "Back to home",
    backButton: "Go back",
    contactButton: "Contact us",
  },
  de: {
    title: "Hoppla! Seite nicht gefunden",
    subtitle: "Fehler 404",
    description: "Die Seite, die Sie suchen, existiert nicht oder wurde verschoben. Versuchen Sie, zur Startseite zurückzukehren.",
    homeButton: "Zurück zur Startseite",
    backButton: "Zurück",
    contactButton: "Kontaktieren Sie uns",
  },
  uk: {
    title: "Ой! Сторінку не знайдено",
    subtitle: "Помилка 404",
    description: "Сторінка, яку ви шукаєте, не існує або була переміщена. Спробуйте повернутися на головну сторінку.",
    homeButton: "На головну",
    backButton: "Назад",
    contactButton: "Зв'яжіться з нами",
  },
  ru: {
    title: "Упс! Страница не найдена",
    subtitle: "Ошибка 404",
    description: "Страница, которую вы ищете, не существует или была перемещена. Попробуйте вернуться на главную страницу.",
    homeButton: "На главную",
    backButton: "Назад",
    contactButton: "Свяжитесь с нами",
  },
  es: {
    title: "¡Ups! Página no encontrada",
    subtitle: "Error 404",
    description: "La página que busca no existe o ha sido movida. Intente volver a la página principal.",
    homeButton: "Volver al inicio",
    backButton: "Volver",
    contactButton: "Contáctenos",
  },
};

const NotFound = () => {
  const location = useLocation();
  const { language } = useLanguage();
  
  // Detect language from URL path
  const detectedLanguage = useMemo((): Language => {
    const pathLang = location.pathname.split('/')[1];
    if (['en', 'de', 'uk', 'ru', 'es'].includes(pathLang)) {
      return pathLang as Language;
    }
    return language || 'sk';
  }, [location.pathname, language]);

  const t = translations[detectedLanguage];

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const handleGoBack = () => {
    if (window.history.length > 2) {
      window.history.back();
    } else {
      window.location.href = getLocalizedPath('/', detectedLanguage);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <div className="text-center max-w-lg mx-auto">
        {/* Animated 404 illustration */}
        <div className="relative mb-8">
          <div className="text-[150px] md:text-[200px] font-bold text-primary/10 leading-none select-none">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-primary/10 rounded-full p-6 animate-pulse">
              <Search className="w-16 h-16 md:w-20 md:h-20 text-primary" />
            </div>
          </div>
        </div>

        {/* Error message */}
        <p className="text-sm font-medium text-primary mb-2 uppercase tracking-wider">
          {t.subtitle}
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
          {t.title}
        </h1>
        <p className="text-muted-foreground mb-8 text-base md:text-lg leading-relaxed">
          {t.description}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={handleGoBack}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backButton}
          </Button>
          <Button asChild className="gap-2">
            <Link to={getLocalizedPath('/', detectedLanguage)}>
              <Home className="w-4 h-4" />
              {t.homeButton}
            </Link>
          </Button>
        </div>

        {/* Contact link */}
        <p className="mt-8 text-sm text-muted-foreground">
          {detectedLanguage === 'sk' ? 'Potrebujete pomoc?' : 
           detectedLanguage === 'en' ? 'Need help?' :
           detectedLanguage === 'de' ? 'Brauchen Sie Hilfe?' :
           detectedLanguage === 'uk' ? 'Потрібна допомога?' :
           detectedLanguage === 'ru' ? 'Нужна помощь?' :
           '¿Necesita ayuda?'}{' '}
          <Link 
            to={getLocalizedPath('/kontakt', detectedLanguage)} 
            className="text-primary hover:underline font-medium"
          >
            {t.contactButton}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
